import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAsyncCarLeasingStepData,
  fetchAsyncCarleasing,
  getAllCarleasingList,
  getAllMotoleasingList,
  getCarLeasingStepData,
  isLoadingCarleasing,
} from "../../redux/transactions/UntappedSlice";

// ── Searchable Car Select Component ───────────────────────────────────────────
function CarSearchSelect({ cars, value, onChange, loading }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = value
    ? value === "__other__"
      ? "➕ Indi modoka"
      : (cars.find((c) => c.id === value)?.type_label ??
         cars.find((c) => c.id === value)?.type ??
         "")
    : "";

  const filtered = query.trim()
    ? cars.filter((c) =>
        (c.type_label ?? c.type ?? "")
          .toLowerCase()
          .includes(query.toLowerCase())
      )
    : cars;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (car) => {
    onChange(car);
    setOpen(false);
    setQuery("");
  };

  const handleOpen = () => {
    if (loading) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className="w-full text-sm px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black text-left flex justify-between items-center disabled:opacity-60"
      >
        <span className={selectedLabel ? "text-black" : "text-gray-500"}>
          {loading ? "Gutegereza..." : selectedLabel || "Hitamo modoka..."}
        </span>
        <svg
          className={`w-4 h-4 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Shakisha modoka..."
                className="flex-1 bg-transparent text-sm text-black dark:text-white outline-none"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              )}
            </div>
          </div>

          <ul className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-500">Nta modoka ibonetse</li>
            ) : (
              filtered.map((car) => (
                <li
                  key={car.id}
                  onClick={() => handleSelect({ type: "car", car })}
                  className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 flex justify-between items-center ${
                    value === car.id ? "bg-blue-50 font-medium text-blue-700 dark:bg-gray-700 dark:text-blue-400" : "text-black dark:text-white"
                  }`}
                >
                  <span>{car.type_label ?? car.type}</span>
                  {car.down_payment != null && (
                    <span className="text-xs text-gray-400">
                      {Number(car.down_payment).toLocaleString()} RWF
                    </span>
                  )}
                  {value === car.id && (
                    <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </li>
              ))
            )}
            <li
              onClick={() => handleSelect({ type: "other" })}
              className={`px-4 py-2.5 text-sm cursor-pointer border-t border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700 ${
                value === "__other__" ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 dark:text-gray-300"
              }`}
            >
              ➕ Hitamo indi modoka
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

function YourIncome({ data, transmission, HandleIncomeData }) {
  const dispatch = useDispatch();



  const allCarleasingListRaw = useSelector(getAllCarleasingList);
  const loadingCarleasing = useSelector(isLoadingCarleasing);
  const currentStepSavedData = useSelector(getCarLeasingStepData);
  const phoneNumber = useSelector((state) => state.untapped.phoneNumber);
  const allMotoleasingList = useSelector(getAllMotoleasingList);

  const allCarleasingList = Array.isArray(allCarleasingListRaw)
    ? allCarleasingListRaw
    : Array.isArray(allCarleasingListRaw?.data)
    ? allCarleasingListRaw.data
    : [];

  const [formData, setFormData] = useState({
    daily_income: "",
    source_of_income: "",
    hasDebt: "",         // "" | true | false
    loan_payment_process: "",
    loan_amount: "",
    deposit_accepted: "", // "" | true | false
    repayment_period: "",
    cooperative: "",
    car_id: "",
    custom_car_name: "",
    custom_car_brand: "",
    car_year: "",
  });

  const [isCustomCar, setIsCustomCar] = useState(false);

  const selectedCar =
    allCarleasingList?.find((c) => c.id === formData.car_id) ?? null;

  const depositAmount =
    selectedCar?.down_payment != null
      ? Number(selectedCar.down_payment) * 0.2
      : null;

  // ── Helper: safely parse a boolean field from saved data ────────────────────
  // Handles: true/false (boolean), "true"/"false" (string), null, undefined
  const parseBool = (val) => {
    if (val === true || val === "true") return true;
    if (val === false || val === "false") return false;
    return ""; // treat null/undefined/anything else as unset
  };

  // ── Hydrate from saved step data ────────────────────────────────────────────
  useEffect(() => {
    if (
      currentStepSavedData?.step_data &&
      Object.keys(currentStepSavedData.step_data).length > 0
    ) {
      const s = currentStepSavedData.step_data;
      const repaymentNumber = parseInt(s.repayment_period ?? "", 10) || "";

      // Support both key names: "hasDebt" and legacy "debts"
      const hasDebtValue =
        s.hasDebt !== undefined ? s.hasDebt : s.debts !== undefined ? s.debts : "";

      const hasCustomCar = !s.car_id && (s.custom_car_name || s.custom_car_brand);
      setIsCustomCar(!!hasCustomCar);

      setFormData((prev) => ({
        ...prev,
        daily_income:        s.daily_income ?? "",
        source_of_income:    s.source_of_income ?? "",
        hasDebt:             parseBool(hasDebtValue),        // ✅ properly parsed
        loan_payment_process: s.loan_payment_process ?? "",
        loan_amount:         s.loan_amount ?? "",
        deposit_accepted:    parseBool(s.deposit_accepted),  // ✅ properly parsed
        repayment_period:    repaymentNumber,
        cooperative:         s.cooperative ?? "",
        car_id:              s.car_id ?? "",
        custom_car_name:     s.custom_car_name ?? "",
        custom_car_brand:    s.custom_car_brand ?? "",
        car_year:            s.car_year ?? "",
      }));
    }
  }, [currentStepSavedData]);

  // ── Fetch step data + car list ───────────────────────────────────────────────
  useEffect(() => {
    if (phoneNumber?.trim().length === 10) {
      dispatch(fetchAsyncCarLeasingStepData({ phone: phoneNumber.trim(), step: 3 }));
    }
  }, [dispatch, phoneNumber]);

  useEffect(() => {
    dispatch(fetchAsyncCarleasing());
  }, [dispatch]);

  // ── Sync incoming `data` prop ────────────────────────────────────────────────
  useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(formData)) {
      setFormData(data);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notify parent (debounced) ────────────────────────────────────────────────
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(formData) !== JSON.stringify(data)) {
        HandleIncomeData(formData);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [formData, data, HandleIncomeData]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Fixed: returns true/false (boolean) or "" (if nothing selected)
  const handleBooleanChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "true" ? true : value === "false" ? false : "",
    }));
  };

  const handleCarSelect = (selection) => {
    if (selection.type === "other") {
      setIsCustomCar(true);
      setFormData((prev) => ({
        ...prev,
        car_id: "",
        custom_car_name: "",
        custom_car_brand: "",
        car_year: "",
      }));
    } else {
      setIsCustomCar(false);
      setFormData((prev) => ({
        ...prev,
        car_id: selection.car.id,
        custom_car_name: "",
        custom_car_brand: "",
      }));
    }
  };

  const inputClass =
    "w-full px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black";
  const labelClass = "block text-sm font-medium mb-2";

  return (
    <form>
      {/* ── Row 1: Source of income + Daily income ── */}
      <div className="flex justify-between items-center gap-4">
        <div className="mb-4 w-1/2">
          <label className={labelClass}>Icyukora</label>
          <input
            type="text"
            name="source_of_income"
            value={formData.source_of_income}
            placeholder="Icyukora"
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div className="mb-4 w-1/2">
          <label className={labelClass}>Amafaranga winjiza ku munsi?</label>
          <input
            type="number"
            name="daily_income"
            value={formData.daily_income}
            placeholder="Amafaranga winjiza ku munsi"
            onChange={handleChange}
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Row 2: Debts + Searchable Car selector ── */}
      <div className="flex justify-between items-center gap-4">
        <div className="mb-4 w-1/2">
          <label className={labelClass}>Ufite indi nguzanyo?</label>
          <select
            name="hasDebt"
            // ✅ converts boolean → string for the select value
            value={formData.hasDebt === "" ? "" : String(formData.hasDebt)}
            onChange={handleBooleanChange}
            className={inputClass}
          >
            <option value="" disabled>Hitamo</option>
            <option value="true">Yego</option>
            <option value="false">Oya</option>
          </select>
        </div>

        <div className="mb-4 w-1/2">
          <label className={labelClass}>
            Hitamo ubwoko bwi <strong>Modoka</strong> ushaka
          </label>
          <CarSearchSelect
            cars={allCarleasingList}
            value={isCustomCar ? "__other__" : formData.car_id}
            onChange={handleCarSelect}
            loading={loadingCarleasing}
          />
          {selectedCar?.down_payment != null && (
            <p className="text-xs text-gray-500 mt-1">
              Igiciro:{" "}
              <span className="font-semibold text-gray-700">
                {Number(selectedCar.down_payment).toLocaleString()} RWF
              </span>
            </p>
          )}
        </div>
      </div>

      {/* ── Custom car: Name + Brand ── */}
      {isCustomCar && (
        <div className="flex justify-between items-center gap-4">
          <div className="mb-4 w-1/2">
            <label className={labelClass}>
              Izina ryi Modoka <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_car_name"
              value={formData.custom_car_name}
              placeholder="Urugero: Corolla, Vitz, RAV4..."
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>
          <div className="mb-4 w-1/2">
            <label className={labelClass}>
              Uruganda (Brand) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_car_brand"
              value={formData.custom_car_brand}
              placeholder="Urugero: Toyota, Honda, Nissan..."
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* ── Car year ── */}
      {(formData.car_id || isCustomCar) && (
        <div className="flex justify-between items-center gap-4">
          <div className="mb-4 w-1/2">
            <label className={labelClass}>
              Umwaka wi Modoka <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="car_year"
              value={formData.car_year}
              placeholder="Urugero: 2019"
              min={2013}
              max={new Date().getFullYear()}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>
          <div className="mb-4 w-1/2" />
        </div>
      )}

      {/* ── Row 3: Loan amount + Repayment period ── */}
      <div className="flex justify-between items-center gap-4">
        <div className="mb-4 w-1/2">
          <label className={labelClass}>Ingano y'inguzanyo (RWF)</label>
          <input
            type="number"
            name="loan_amount"
            value={formData.loan_amount}
            placeholder="Ingano y'inguzanyo"
            min={1}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div className="mb-4 w-1/2">
          <label className={labelClass}>
            Igihe uzi shyuramo (amezi) — 4 kugeza 48
          </label>
          <input
            type="number"
            name="repayment_period"
            value={formData.repayment_period}
            placeholder="Urugero: 24"
            min={4}
            max={48}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Row 4: Payment process + Deposit accepted ── */}
      <div className="flex justify-between items-center gap-4">
        <div className="mb-4 w-1/2">
          <label className={labelClass}>Uzishyura mubuhe buryo</label>
          <select
            name="loan_payment_process"
            value={formData.loan_payment_process}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="" disabled>Hitamo</option>
            <option value="weekly">Buri cy'umweru / Weekly</option>
            <option value="monthly">Buri kwezi / Monthly</option>
          </select>
        </div>
        <div className="mb-4 w-1/2">
          <label className={labelClass}>
            Wemeye kwishyura 20% ➕ byigiciro cyi modoka?
            {depositAmount != null && (
              <span className="ml-1 font-normal text-gray-500">
                (= {depositAmount.toLocaleString()} RWF)
              </span>
            )}
          </label>
          <select
            name="deposit_accepted"
            // ✅ converts boolean → string for the select value
            value={formData.deposit_accepted === "" ? "" : String(formData.deposit_accepted)}
            onChange={handleBooleanChange}
            className={inputClass}
          >
            <option value="" disabled>Hitamo</option>
            <option value="true">Yego</option>
            <option value="false">Oya</option>
          </select>
          {formData.deposit_accepted === false && (
            <p className="text-xs text-red-500 mt-1">
              Ugomba kwishyura deposit kugira ngo ubonere inguzanyo.
            </p>
          )}
        </div>
      </div>
    </form>
  );
}

export default YourIncome;